
import React, { useState, useEffect } from 'react';
import { UserData, SystemReport, DiscountCode } from '../types';
import { supabase } from '../services/supabaseClient';
import { AdminPromptsView } from './AdminPromptsView';

interface AdminDashboardProps {
  reports?: SystemReport[];
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ reports = [] }) => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [masterCode, setMasterCode] = useState('');
  const [loginError, setLoginError] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'reports' | 'vouchers' | 'system' | 'prompts'>('users');
  
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Voucher state persistent în localStorage pentru demo
  const [vouchers, setVouchers] = useState<DiscountCode[]>(() => {
    const saved = localStorage.getItem('lexsmart_vouchers');
    return saved ? JSON.parse(saved) : [
      { code: 'LEXSMART20', value: 20, type: 'percentage', expiryDate: '2025-12-31', maxUses: 100, currentUses: 0, isActive: true },
      { code: 'WELCOME10', value: 10, type: 'fixed', expiryDate: '2024-12-31', maxUses: 50, currentUses: 5, isActive: true }
    ];
  });

  const [newVoucher, setNewVoucher] = useState({ code: '', value: '', type: 'percentage', expiry: '' });

  useEffect(() => {
    localStorage.setItem('lexsmart_vouchers', JSON.stringify(vouchers));
  }, [vouchers]);

  useEffect(() => {
    if (isAdminAuthenticated && activeTab === 'users') {
      fetchUsers();
    }
  }, [isAdminAuthenticated, activeTab]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    // Presupunând că ai o politică RLS sau acces root din client (atenție în producție)
    // Vom citi din tabela profiles
    const { data, error } = await supabase.from('profiles').select('*');
    if (!error && data) {
      setUsers(data);
    } else {
      console.error("Error fetching users:", error);
    }
    setLoadingUsers(false);
  };

  const handleApproveUser = async (userId: string) => {
    const { error } = await supabase.from('profiles').update({ is_approved: true }).eq('id', userId);
    if (!error) {
      setUsers(users.map(u => u.id === userId ? { ...u, is_approved: true } : u));
    } else {
      console.error("Error approving user:", error);
      alert("Eroare la aprobarea utilizatorului.");
    }
  };

  const handleSuspendUser = async (userId: string) => {
     const { error } = await supabase.from('profiles').update({ is_approved: false }).eq('id', userId);
    if (!error) {
      setUsers(users.map(u => u.id === userId ? { ...u, is_approved: false } : u));
    } else {
      console.error("Error suspending user:", error);
      alert("Eroare la suspendarea utilizatorului.");
    }
  };

  const SECRET_KEY = import.meta.env.VITE_ADMIN_SECRET || '';

  const handleAdminLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (masterCode.trim() === SECRET_KEY.trim()) {
      setIsAdminAuthenticated(true);
      setLoginError(false);
    } else {
      setLoginError(true);
      setTimeout(() => setLoginError(false), 2000);
    }
  };

  const handleAddVoucher = () => {
    if (!newVoucher.code || !newVoucher.value) return;
    const v: DiscountCode = {
      code: newVoucher.code.toUpperCase().trim(),
      value: Number(newVoucher.value),
      type: newVoucher.type as any,
      expiryDate: newVoucher.expiry || '2025-12-31',
      maxUses: 1000,
      currentUses: 0,
      isActive: true
    };
    setVouchers([...vouchers, v]);
    setNewVoucher({ code: '', value: '', type: 'percentage', expiry: '' });
  };

  const toggleVoucher = (code: string) => {
    setVouchers(vouchers.map(v => v.code === code ? { ...v, isActive: !v.isActive } : v));
  };

  const deleteVoucher = (code: string) => {
    setVouchers(vouchers.filter(v => v.code !== code));
  };

  if (!isAdminAuthenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <div className="max-w-md w-full animate-in zoom-in-95 duration-500">
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-indigo-600 rounded-[2rem] flex items-center justify-center text-white text-3xl font-black mx-auto mb-6 shadow-2xl shadow-indigo-500/20">L</div>
            <h1 className="text-white text-2xl font-black uppercase tracking-[0.4em] italic leading-none">LexGuard</h1>
            <p className="text-indigo-400 text-[10px] font-bold uppercase tracking-widest mt-2">Sistem de Administrare Securizat</p>
          </div>
          <form onSubmit={handleAdminLogin} className="space-y-6">
            <div className="bg-white/5 p-8 rounded-[2.5rem] border border-white/10 backdrop-blur-xl">
              <label className="block text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-4 ml-1">Cod Acces Master</label>
              <input 
                type="password"
                autoFocus
                value={masterCode}
                onChange={(e) => setMasterCode(e.target.value)}
                className={`w-full bg-black/40 border ${loginError ? 'border-rose-500 shadow-rose-500/20' : 'border-white/10 focus:border-indigo-500'} rounded-2xl p-5 text-white text-center font-black tracking-[0.5em] outline-none transition-all`}
                placeholder="****-****-****"
              />
              {loginError && <p className="text-rose-500 text-[10px] font-black uppercase text-center mt-4 animate-bounce italic">Acces Refuzat</p>}
            </div>
            <button type="submit" className="w-full py-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-sm uppercase tracking-[0.2em] transition-all active:scale-95 shadow-2xl shadow-indigo-600/20">
              Verifică Jurisdicția
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col md:flex-row">
      <aside className="w-full md:w-72 bg-black flex flex-col shrink-0 border-r border-white/5 overflow-y-auto">
        <div className="p-10 border-b border-white/5 flex items-center gap-4">
           <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-black">L</div>
           <div>
             <h2 className="text-white font-black tracking-tight italic text-sm">LexAdmin</h2>
             <p className="text-[8px] text-indigo-400 font-bold uppercase tracking-widest">Control Central</p>
           </div>
        </div>
        <nav className="p-6 space-y-2 flex-grow">
          {[
            { id: 'users', label: 'Utilizatori', icon: '📊' },
            { id: 'prompts', label: 'AI Prompting', icon: '🤖' },
            { id: 'vouchers', label: 'Vouchere & Reduceri', icon: '🎟️' },
            { id: 'reports', label: 'Sesizări', icon: '🚩' },
            { id: 'system', label: 'Sistem', icon: '⚙️' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`w-full text-left px-5 py-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-4 ${activeTab === tab.id ? 'bg-indigo-600 text-white shadow-lg' : 'text-white/40 hover:bg-white/5 hover:text-white'}`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
        <div className="p-8">
           <button onClick={() => setIsAdminAuthenticated(false)} className="w-full py-4 border border-white/10 rounded-xl text-[9px] font-black text-white/40 uppercase tracking-widest hover:bg-rose-500 hover:text-white transition-all">Deconectare Admin</button>
        </div>
      </aside>

      <main className="flex-grow p-6 md:p-12 overflow-auto bg-[#0F172A] custom-scrollbar">
        {activeTab === 'prompts' && (
          <AdminPromptsView />
        )}

        {activeTab === 'vouchers' && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-10 max-w-5xl">
            <div className="flex justify-between items-end">
              <div>
                <h3 className="text-white text-3xl font-black italic tracking-tight">Campanii de Discount</h3>
                <p className="text-indigo-400 text-[10px] font-black uppercase tracking-widest mt-2 italic">Atracție și retenție avocați</p>
              </div>
            </div>
            
            <div className="bg-white/5 p-6 md:p-10 rounded-[2.5rem] border border-white/10 grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
               <div className="space-y-2">
                 <label className="text-[8px] font-black text-indigo-300 uppercase tracking-widest ml-1">Cod (ex: SUM-25)</label>
                 <input className="w-full bg-black/40 border border-white/10 rounded-xl p-4 text-white font-black outline-none focus:border-indigo-500 transition-all" placeholder="START2024" value={newVoucher.code} onChange={e => setNewVoucher({...newVoucher, code: e.target.value})} />
               </div>
               <div className="space-y-2">
                 <label className="text-[8px] font-black text-indigo-300 uppercase tracking-widest ml-1">Valoare</label>
                 <input type="number" className="w-full bg-black/40 border border-white/10 rounded-xl p-4 text-white font-black outline-none focus:border-indigo-500 transition-all" placeholder="20" value={newVoucher.value} onChange={e => setNewVoucher({...newVoucher, value: e.target.value})} />
               </div>
               <div className="space-y-2">
                 <label className="text-[8px] font-black text-indigo-300 uppercase tracking-widest ml-1">Tip Discount</label>
                 <select aria-label="Tip Discount" className="w-full bg-black/40 border border-white/10 rounded-xl p-4 text-white font-black outline-none appearance-none" value={newVoucher.type} onChange={e => setNewVoucher({...newVoucher, type: e.target.value})}>
                   <option value="percentage">% Procentual</option>
                   <option value="fixed">€ Sumă Fixă</option>
                 </select>
               </div>
               <button onClick={handleAddVoucher} className="bg-indigo-600 hover:bg-indigo-700 text-white h-[60px] rounded-xl font-black uppercase tracking-widest text-[10px] shadow-xl shadow-indigo-500/10 transition-all active:scale-95">Publică Voucher</button>
            </div>

            <div className="bg-black/40 rounded-[2.5rem] border border-white/5 overflow-hidden">
              <table className="w-full text-left text-[11px]">
                <thead>
                  <tr className="border-b border-white/5 bg-white/5 text-indigo-400 font-black uppercase">
                    <th className="px-10 py-6">Cod Activ</th>
                    <th className="px-6 py-6">Valoare / Tip</th>
                    <th className="px-6 py-6">Status</th>
                    <th className="px-10 py-6 text-right">Acțiuni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-white/70">
                  {vouchers.map(v => (
                    <tr key={v.code} className="hover:bg-white/5 transition-colors group">
                      <td className="px-10 py-6">
                        <span className="font-black text-white text-lg italic tracking-tight">{v.code}</span>
                      </td>
                      <td className="px-6 py-6">
                        <span className="font-bold">{v.value} {v.type === 'percentage' ? '%' : '€'}</span>
                        <p className="text-[8px] text-white/20 uppercase tracking-widest mt-1">Valabilitate: {v.expiryDate}</p>
                      </td>
                      <td className="px-6 py-6">
                        <span className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest ${v.isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                          {v.isActive ? 'Activ' : 'Suspendat'}
                        </span>
                      </td>
                      <td className="px-10 py-6 text-right space-x-4">
                        <button onClick={() => toggleVoucher(v.code)} className="text-indigo-400 hover:text-white font-black uppercase text-[9px] tracking-widest transition-all">
                          {v.isActive ? 'Dezactivează' : 'Activează'}
                        </button>
                        <button onClick={() => deleteVoucher(v.code)} className="text-rose-400 hover:text-rose-300 font-black uppercase text-[9px] tracking-widest transition-all">Șterge</button>
                      </td>
                    </tr>
                  ))}
                  {vouchers.length === 0 && (
                    <tr><td colSpan={4} className="px-10 py-20 text-center text-white/10 font-black uppercase tracking-[0.4em] italic text-xl">Niciun voucher activ</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h3 className="text-white text-3xl font-black italic tracking-tight mb-8">Utilizatori Înregistrați</h3>
            <div className="bg-black/40 rounded-[2.5rem] border border-white/5 overflow-hidden backdrop-blur-md">
              <table className="w-full text-left text-[11px]">
                <thead>
                  <tr className="border-b border-white/5 bg-white/5 font-black text-indigo-400 uppercase tracking-widest">
                    <th className="px-10 py-6">Identitate Avocat</th>
                    <th className="px-6 py-6">Status Cont</th>
                    <th className="px-6 py-6">Voucher Folosit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-white/70">
                  {loadingUsers ? (
                    <tr><td colSpan={4} className="px-10 py-10 text-center text-indigo-400 font-bold italic">Se încarcă conturile...</td></tr>
                  ) : users.map(u => (
                    <tr key={u.id} className="hover:bg-white/5 transition-colors group">
                      <td className="px-10 py-6">
                        <p className="font-black text-white text-lg tracking-tight italic">{u.name}</p>
                        <p className="text-[10px] font-bold text-slate-400 mt-1">Barou: {u.bar} | Legitimație: {u.bar_card_number}</p>
                      </td>
                      <td className="px-6 py-6">
                        <span className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest ${u.is_approved ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                          {u.is_approved ? 'Aprobat' : 'În Așteptare'}
                        </span>
                      </td>
                      <td className="px-6 py-6">
                        {!u.is_approved ? (
                          <button onClick={() => handleApproveUser(u.id)} className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all">
                            Aprobă Acces
                          </button>
                        ) : (
                          <button onClick={() => handleSuspendUser(u.id)} className="bg-rose-600/20 hover:bg-rose-600 text-rose-400 hover:text-white px-4 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all">
                            Suspendă
                          </button>
                        )}
                      </td>
                  </tr>
                  ))}
                  {!loadingUsers && users.length === 0 && (
                    <tr><td colSpan={4} className="px-10 py-10 text-center text-slate-500 font-bold italic">Niciun utilizator găsit.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
